import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircleQuestion, X, Send, LifeBuoy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Masqué sur les espaces de consultation/médium pour ne pas gêner le chat en direct.
const HIDDEN = ["/consultation", "/medium", "/acces-medium"];

export const SupportChat = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  if (HIDDEN.some((p) => pathname.startsWith(p))) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/support/messages`, {
        name: user?.name || "", email: user?.email || email, message,
      });
      setSent(true); setMessage("");
    } catch {
      toast.error("Envoi impossible pour le moment, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="support-chat-button"
        aria-label="Besoin d'aide ?"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full pl-4 pr-5 py-3 shadow-[0_16px_40px_-10px_rgba(23,19,52,0.6)] gold-glow"
        style={{ background: "linear-gradient(135deg,#241d54,#3a2f7a)", border: "1px solid rgba(212,175,55,0.5)" }}>
        <MessageCircleQuestion className="w-5 h-5 text-goldsoft" strokeWidth={1.7} />
        <span className="text-[#f2f0ff] text-sm font-medium">Un souci ?</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }} data-testid="support-chat-panel"
            className="fixed bottom-24 right-5 z-40 w-[min(92vw,360px)] rounded-3xl overflow-hidden glass shadow-2xl">
            <div className="px-5 py-4 flex items-center justify-between" style={{ background: "linear-gradient(135deg,#241d54,#3a2f7a)" }}>
              <div className="flex items-center gap-2.5">
                <LifeBuoy className="w-5 h-5 text-goldsoft" strokeWidth={1.6} />
                <div>
                  <p className="text-[#f2f0ff] font-serif text-lg leading-tight">Besoin d'aide ?</p>
                  <p className="text-[#c9c4ea] text-[11px]">On vous répond 7j/7</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="support-chat-close" aria-label="Fermer" className="text-[#c9c4ea] hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {sent ? (
                <div className="text-center py-4" data-testid="support-chat-sent">
                  <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-3"><Send className="w-5 h-5 text-green-600" /></div>
                  <p className="text-[var(--text)] font-medium mb-1">Message envoyé !</p>
                  <p className="text-[var(--muted)] text-sm">Nous revenons vers vous très vite par email.</p>
                  <button onClick={() => { setSent(false); setOpen(false); }} className="btn-gold mt-5 px-6 py-2.5 rounded-full text-sm font-medium">Fermer</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="support-chat-form">
                  <p className="text-[var(--muted)] text-sm leading-relaxed">Une question, un souci de paiement ou de connexion ? Écrivez-nous, on s'occupe de tout.</p>
                  {!user && (
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="support-chat-email"
                      placeholder="Votre email" className="w-full bg-white border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm focus:border-gold outline-none" />
                  )}
                  <textarea required rows={4} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="support-chat-message"
                    placeholder="Décrivez votre souci..." className="w-full bg-white border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm focus:border-gold outline-none resize-none" />
                  <button disabled={loading} data-testid="support-chat-submit" className="btn-gold w-full py-3 rounded-full text-sm font-medium flex items-center justify-center gap-2">{loading ? "..." : <><Send className="w-4 h-4" /> Envoyer</>}</button>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
